import React, { useState, useEffect } from "react";
import { db } from "@/api/db";
import { Radio, Calendar as CalendarIcon, ArrowLeft, Clock, Search, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ptBR } from "date-fns/locale";
import { STATUS_CONFIG, CATEGORIES, CATEGORY_COLORS } from "@/lib/live-constants";

function getEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  return url;
}

function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}


export default function LiveLessons() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [dateRange, setDateRange] = useState(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [playerLesson, setPlayerLesson] = useState(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    const data = await db.entities.Lesson.filter({ type: "live", is_published: true }, "live_date");
    setLessons(data.filter(l => l.live_status !== "recording"));
    setLoading(false);
  }

  function handleRangeSelect(range) {
    setDateRange(range);
    // fecha o popover só quando o range estiver completo (from + to)
    if (range?.from && range?.to) setCalendarOpen(false);
  }

  function clearFilters() {
    setSearch("");
    setActiveCategory("all");
    setDateRange(undefined);
  }

  const hasFilters = search || activeCategory !== "all" || dateRange?.from;

  const filtered = lessons.filter(l => {
    const matchCategory = activeCategory === "all" || l.category === activeCategory;
    const matchSearch = !search ||
      l.title?.toLowerCase().includes(search.toLowerCase()) ||
      l.description?.toLowerCase().includes(search.toLowerCase());
    const lessonDate = l.live_date ? new Date(l.live_date) : null;
    const matchDate = !dateRange?.from || (
      lessonDate && lessonDate >= dateRange.from &&
      (!dateRange.to || lessonDate <= new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59))
    );
    return matchCategory && matchSearch && matchDate;
  });

  // ── Player view ──────────────────────────────────────────────────
  if (playerLesson) {
    const statusKey = playerLesson.live_status || "upcoming";
    const sc = STATUS_CONFIG[statusKey];
    const catColor = CATEGORY_COLORS[playerLesson.category] || { bg: "#F2F4F4", text: "#646C6F" };
    const activeUrl = statusKey === "recording"
      ? (playerLesson.video_url || playerLesson.live_url)
      : playerLesson.live_url;
    const embedUrl = getEmbedUrl(activeUrl);

    return (
      <div className="max-w-4xl mx-auto space-y-5">
        <button
          onClick={() => setPlayerLesson(null)}
          className="flex items-center gap-1.5 text-sm font-medium"
          style={{ color: "#646C6F" }}
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para encontros ao vivo
        </button>

        {/* Player */}
        <div className="rounded-2xl overflow-hidden shadow-md" style={{ background: "#1A1D1E" }}>
          <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0"
              style={{ background: sc.bg, color: sc.text }}
            >
              {statusKey === "live" && (
                <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: sc.text }} />
              )}
              {sc.label}
            </span>
            {playerLesson.category && (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0"
                style={{ background: catColor.bg, color: catColor.text }}
              >
                {playerLesson.category}
              </span>
            )}
            <p className="text-white text-sm font-medium truncate">{playerLesson.title}</p>
          </div>

          {embedUrl ? (
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe
                key={embedUrl}
                src={embedUrl}
                title={playerLesson.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "#FEEDB4" }}>
                <Clock className="w-8 h-8" style={{ color: "#65541B" }} />
              </div>
              <p className="text-sm" style={{ color: "#99A6AB" }}>
                Aula ainda não aconteceu — fique atento!
              </p>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="rounded-2xl p-5 space-y-2" style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
          <h2 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#2E3233" }}>
            {playerLesson.title}
          </h2>
          {playerLesson.live_date && (
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#99A6AB" }} />
              <span className="text-sm" style={{ color: "#646C6F" }}>
                {new Date(playerLesson.live_date).toLocaleString("pt-BR", {
                  day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </span>
            </div>
          )}
          {playerLesson.description && (
            <p className="text-sm leading-relaxed" style={{ color: "#646C6F" }}>
              {playerLesson.description}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Listagem ─────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto space-y-6">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Encontros ao Vivo
        </h1>
        <p className="text-sm mt-0.5" style={{ color: "#99A6AB" }}>
          Lives, tutorias, roleplays e demais sessões agendadas.
        </p>
      </div>

      {/* Busca + filtros numa linha */}
      <div className="flex gap-3 flex-wrap items-center">

        {/* Busca */}
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#99A6AB" }} />
          <input
            type="text"
            placeholder="Buscar aulas..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-full text-sm focus:outline-none"
            style={{ border: "1px solid #E5E9EA", background: "#fff", color: "#2E3233" }}
            onFocus={e => e.target.style.borderColor = "#00DCA4"}
            onBlur={e => e.target.style.borderColor = "#E5E9EA"}
          />
        </div>

        {/* Filtro de data — Popover com Calendar */}
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <button
              className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all flex-shrink-0"
              style={dateRange?.from
                ? { background: "#EAFAF6", color: "#00664C", border: "2px solid #00DCA4" }
                : { background: "#fff", color: "#646C6F", border: "1px solid #E5E9EA" }}
            >
              <CalendarIcon className="w-4 h-4 flex-shrink-0" />
              {dateRange?.from ? (
                dateRange.to
                  ? `${dateRange.from.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} → ${dateRange.to.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}`
                  : dateRange.from.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })
              ) : "Filtrar por período"}
              {dateRange?.from && (
                <span
                  onClick={e => { e.stopPropagation(); setDateRange(undefined); }}
                  className="w-4 h-4 rounded-full flex items-center justify-center ml-0.5"
                  style={{ background: "#00664C", color: "#fff" }}
                >
                  <X className="w-2.5 h-2.5" />
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleRangeSelect}
              locale={ptBR}
              numberOfMonths={2}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Filtro de categorias */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setActiveCategory("all")}
          className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
          style={activeCategory === "all"
            ? { background: "#003326", color: "#fff" }
            : { background: "#F2F4F4", color: "#646C6F" }}
        >
          Todas as categorias
        </button>
        {CATEGORIES.map(cat => {
          const active = activeCategory === cat;
          const cc = CATEGORY_COLORS[cat];
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(active ? "all" : cat)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
              style={active
                ? { background: cc.bg, color: cc.text, outline: "2px solid #00DCA4", outlineOffset: "2px" }
                : { background: "#F2F4F4", color: "#646C6F" }}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* Contagem + limpar filtros */}
      {!loading && (
        <div className="flex items-center justify-between">
          <p className="text-sm" style={{ color: "#99A6AB" }}>
            {filtered.length} {filtered.length === 1 ? "aula encontrada" : "aulas encontradas"}
          </p>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-xs font-semibold"
              style={{ color: "#00664C" }}
            >
              <X className="w-3 h-3" /> Limpar filtros
            </button>
          )}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl p-16 flex flex-col items-center gap-3 text-center"
          style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
          <Radio className="w-10 h-10 opacity-20" style={{ color: "#00664C" }} />
          <p className="font-medium" style={{ color: "#99A6AB" }}>Nenhuma aula encontrada para esses filtros.</p>
          <button
            onClick={clearFilters}
            className="text-xs font-semibold px-4 py-2 rounded-full"
            style={{ background: "#EAFAF6", color: "#00664C" }}
          >
            Limpar filtros
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map(lesson => {
            const statusKey = lesson.live_status || "upcoming";
            const sc = STATUS_CONFIG[statusKey];
            const catColor = CATEGORY_COLORS[lesson.category] || null;
            const d = lesson.live_date ? new Date(lesson.live_date) : null;
            const dayNum = d ? d.getDate() : null;
            const monthShort = d ? d.toLocaleString("pt-BR", { month: "short" }).replace(".", "").toUpperCase() : null;
            const timeStr = d ? d.toLocaleString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : null;
            return (
              <div
                key={lesson.id}
                className="rounded-2xl relative overflow-hidden cursor-pointer transition-transform duration-300 hover:scale-[1.03]"
                style={{ aspectRatio: "3/4" }}
                onClick={() => setPlayerLesson(lesson)}
              >
                {/* Imagem full ou fallback */}
                {lesson.image_url ? (
                  <img src={lesson.image_url} alt={lesson.title} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0" style={{ background: sc.gradient }}>
                    <Radio className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 text-white opacity-20" />
                  </div>
                )}

                {/* Gradient overlay */}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(transparent 35%, rgba(0,0,0,0.85) 100%)' }} />

                {/* Chip flutuante: ao vivo OU data+hora */}
                {statusKey === "live" ? (
                  <span
                    className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold"
                    style={{ background: sc.bg, color: sc.text }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: sc.text }} />
                    {sc.label}
                  </span>
                ) : d ? (
                  <span
                    className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium"
                    style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', color: '#fff' }}
                  >
                    <CalendarIcon className="w-3.5 h-3.5" />
                    {dayNum} {monthShort} · {timeStr}
                  </span>
                ) : null}

                {/* Badge gravação — top-right */}
                {statusKey === "recording" && (
                  <span
                    className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-semibold"
                    style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(8px)', color: '#EAFAF6' }}
                  >
                    Gravação
                  </span>
                )}

                {/* Content — bottom */}
                <div className="absolute bottom-0 left-0 right-0 p-4 flex flex-col gap-1.5">
                  {catColor && lesson.category && (
                    <span
                      className="self-start px-2 py-0.5 rounded-full text-[10px] font-semibold"
                      style={{ background: catColor.bg, color: catColor.text }}
                    >
                      {lesson.category}
                    </span>
                  )}
                  <h3 className="text-white font-bold text-sm line-clamp-2 leading-snug" title={lesson.title}>
                    {lesson.title}
                  </h3>
                  {lesson.presenter && (
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      Apresentado por {lesson.presenter}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
